import { createFileRoute, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStore } from '../../../store';
import layoutStyles from '../../../components/ProjectsView.module.css';
import styles from '../../../components/ProjectSecurity.module.css';
import { waitForAck } from '../../../utils/waitForAck';
import { send, waitForReady } from '../../../ws';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { CopyButton } from '../../../components/shared/CopyButton';

interface ShareLink {
  id: string;
  projectId: string;
  appId?: string;
  versionId?: string;
  createdAt: number;
  expiresAt: number;
  createdBy?: string;
  status: string;
  hasPasscode: boolean;
  isExpired: boolean;
}

export const Route = createFileRoute('/projects/$projectId/share-links')({
  component: ProjectShareLinksTab,
});

function ProjectShareLinksTab() {
  const { projectId } = useParams({ from: '/projects/$projectId/share-links' });
  const project = useStore(s => s.projects[projectId]);
  const applications = useStore(s => s.applications);

  const [shares, setShares] = useState<ShareLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<ShareLink | null>(null);

  const activeShares = shares.filter(s => s.status === 'active' && !s.isExpired);
  const expiredShares = shares.filter(s => s.status !== 'active' || s.isExpired);

  const loadShares = useCallback(async () => {
    try {
      await waitForReady();
      const corr = uuidv4();
      send('preview.share.list', { projectId }, corr);
      const result = await waitForAck<{ ok: boolean; shares: ShareLink[] }>(corr);
      setShares(result.shares);
    } catch (error: any) {
      setStatusMessage(error?.message || 'Failed to load share links');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  if (!project) return null;

  const handleRevoke = async () => {
    if (!revokeTarget) return;

    try {
      await waitForReady();
      const corr = uuidv4();
      send('preview.share.revoke', { id: revokeTarget.id }, corr);
      await waitForAck<{ ok: boolean }>(corr);

      setShares(prev => prev.filter(s => s.id !== revokeTarget.id));
      setRevokeTarget(null);
      setStatusMessage('Share link revoked.');
    } catch (error: any) {
      setStatusMessage(error?.message || 'Failed to revoke share link');
      setRevokeTarget(null);
    }
  };

  const getShareUrl = (shareId: string) =>
    `${window.location.origin}/preview/${shareId}`;

  const getAppName = (appId?: string) => {
    if (!appId) return 'Unknown app';
    return applications[appId]?.name ?? appId;
  };

  const formatTimeRemaining = (expiresAt: number) => {
    if (expiresAt === 0) return 'Never expires';
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) return 'Expired';
    const minutes = Math.floor(remaining / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h remaining`;
    if (hours > 0) return `${hours}h ${minutes % 60}m remaining`;
    return `${minutes}m remaining`;
  };

  const formatDate = (timestamp: number) =>
    new Date(timestamp).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const renderShareCard = (share: ShareLink) => (
    <div key={share.id} className={styles.tokenCard}>
      <div className={styles.tokenCardHeader}>
        <div className={styles.tokenCardInfo}>
          <span className={styles.tokenCardName}>
            {getAppName(share.appId)}
            {share.versionId && (
              <span className={styles.keyMeta} style={{ marginLeft: 'var(--space-2)' }}>
                v{share.versionId}
              </span>
            )}
            {share.hasPasscode && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ marginLeft: 'var(--space-1)', verticalAlign: 'middle', opacity: 0.5 }}
              >
                <rect x="3" y="11" width="18" height="11" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            )}
          </span>
          <span className={styles.tokenCardDesc}>
            {formatDate(share.createdAt)}
            {' · '}
            {formatTimeRemaining(share.expiresAt)}
          </span>
        </div>
        <div className={styles.tokenCardActions}>
          <a
            href={`/analytics/?previewShareId=${encodeURIComponent(share.id)}`}
            className={styles.keyLink}
          >
            Analytics
          </a>
          <button
            type="button"
            className={`${styles.keyLink} ${styles.keyLinkDanger}`}
            onClick={() => setRevokeTarget(share)}
          >
            Revoke
          </button>
        </div>
      </div>
      <div className={styles.tokenCardBody}>
        <code className={styles.tokenCardCode}>{getShareUrl(share.id)}</code>
        <CopyButton value={getShareUrl(share.id)} aria-label="Copy share link" />
      </div>
    </div>
  );

  return (
    <div className={styles.wrapper}>
      {/* Active Share Links */}
      <div className={styles.keySection}>
        <div className={styles.keySectionHeader}>
          <div className={styles.keySectionTitle}>Active share links</div>
          <button
            type="button"
            className={styles.keySectionAdd}
            onClick={loadShares}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing\u2026' : 'Refresh'}
          </button>
        </div>

        {isLoading ? (
          <div className={styles.emptyState}>Loading...</div>
        ) : activeShares.length === 0 ? (
          <div className={styles.emptyState}>
            No active share links. Create one from an application's version menu.
          </div>
        ) : (
          activeShares.map(renderShareCard)
        )}
      </div>

      {/* Expired / Revoked */}
      {expiredShares.length > 0 && (
        <div className={styles.keySection}>
          <div className={styles.keySectionHeader}>
            <div className={styles.keySectionTitle}>Expired</div>
          </div>
          {expiredShares.map(share => (
            <div key={share.id} className={styles.keyRow}>
              <div className={styles.keyInfo}>
                <div className={styles.keyNameRow}>
                  <span className={styles.keyName}>{getAppName(share.appId)}</span>
                  <span className={styles.keyMeta}>{formatDate(share.createdAt)}</span>
                </div>
                {share.versionId && (
                  <div className={styles.keyDescription}>v{share.versionId}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {statusMessage && (
        <div className={layoutStyles.errorMessage}>
          <span className={layoutStyles.errorText}>{statusMessage}</span>
          <button
            type="button"
            onClick={() => setStatusMessage('')}
            className={layoutStyles.errorClose}
            aria-label="Dismiss status"
          >
            ×
          </button>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!revokeTarget}
        title="Revoke Share Link"
        message="Anyone with this link will no longer be able to access the preview. This cannot be undone."
        confirmText="Revoke"
        cancelText="Cancel"
        onConfirm={handleRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  );
}
